// SagaPay Checkout - Main App v1.0.1
// Inline checkout widget for e-commerce integration
// Direct access protection enabled

import { useEffect, useState } from 'react';
import { useCheckoutStore, useProgressPercentage, dispatchToConfig } from './store/checkout';
import { ThemeProvider } from './store/theme';
import { getStoreDetails, getStoreDetailsFromSession, getMerchantPaymentMethods, getSession } from './api/client';
import { IdentityStep } from './steps/IdentityStep';
import { AddressStep } from './steps/AddressStep';
import { ShippingStep } from './steps/ShippingStep';
import { PaymentStep, resetSdkState } from './steps/PaymentStep';
import { SuccessStep } from './steps/SuccessStep';
import { OrderSummary } from './components/OrderSummary';
import { ProgressBar } from './components/ProgressBar';
import { ErrorBoundary } from './components/ErrorBoundary';
import type { CheckoutConfig } from './types/config';
import './styles/global.css';
import styles from './App.module.css';

// Module-level guards: survive component remounts caused by store updates
let checkoutInitInProgress = false;
let checkoutInitDone = false;

// Global config from parent
declare global {
  interface Window {
    SagaPayCheckoutConfig?: CheckoutConfig;
  }
}

// Helper to notify parent window/callbacks
function notifyParent(type: string, data: unknown) {
  // Send to parent window (for iframe) - use document.referrer for security
  if (window.parent !== window) {
    try {
      const parentOrigin = document.referrer 
        ? new URL(document.referrer).origin 
        : window.location.origin;
      window.parent.postMessage({ type: `SAGAPAY_${type}`, data }, parentOrigin);
    } catch {
      window.parent.postMessage({ type: `SAGAPAY_${type}`, data }, window.location.origin);
    }
  }
  
  // Send to opener window (for Apple Pay new-window fallback)
  if (window.opener) {
    try {
      window.opener.postMessage({ type: `SAGAPAY_${type}`, data }, '*');
    } catch {
      // Opener may have closed
    }
  }
  
  // Dispatch custom event (for inline widget)
  window.dispatchEvent(new CustomEvent(`sagapay:${type.toLowerCase()}`, { detail: data }));

  // And the merchant's own callbacks. This local copy carried the transport
  // only, so CHECKOUT_READY and CHECKOUT_ERROR emitted here reached the bridge
  // and never reached onReady or onError.
  dispatchToConfig(type, data);
}

// Anti-tamper: ensure "Powered by SagaPay" branding stays visible
function useBrandingProtection() {
  useEffect(() => {
    const protect = () => {
      const el = document.querySelector('[data-sagapay-branding="true"]') as HTMLElement;
      if (!el) return;
      // Force visible styles if anyone tampers via JS/CSS
      const forced: Record<string, string> = {
        display: 'flex',
        visibility: 'visible',
        opacity: '1',
        position: 'relative',
        height: 'auto',
        width: 'auto',
        overflow: 'visible',
        'clip-path': 'none',
        transform: 'none',
        'pointer-events': 'auto',
      };
      for (const [prop, val] of Object.entries(forced)) {
        if (el.style.getPropertyValue(prop) !== val) {
          el.style.setProperty(prop, val, 'important');
        }
      }
    };

    // MutationObserver to catch DOM/style changes
    const observer = new MutationObserver(protect);
    const target = document.querySelector('[data-sagapay-branding="true"]');
    if (target) {
      observer.observe(target, { attributes: true, attributeFilter: ['style', 'class'], childList: true });
      // Also watch parent in case they try to hide the footer
      if (target.parentElement) {
        observer.observe(target.parentElement, { attributes: true, attributeFilter: ['style', 'class'] });
      }
    }
    // Periodic check every 2s as fallback
    const interval = setInterval(protect, 2000);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);
}

function CheckoutContent({ widgetMode }: { widgetMode?: boolean }) {
  useBrandingProtection();
  const currentStep = useCheckoutStore((s) => s.currentStep);
  const initSession = useCheckoutStore((s) => s.initSession);
  const setCustomer = useCheckoutStore((s) => s.setCustomer);
  const setShippingAddress = useCheckoutStore((s) => s.setShippingAddress);
  const setBillingAddress = useCheckoutStore((s) => s.setBillingAddress);
  const setCustomShippingOptions = useCheckoutStore((s) => s.setCustomShippingOptions);
  const setUseBuiltInShipping = useCheckoutStore((s) => s.setUseBuiltInShipping);
  const setSkipShipping = useCheckoutStore((s) => s.setSkipShipping);
  const setTheme = useCheckoutStore((s) => s.setTheme);
  const setLocale = useCheckoutStore((s) => s.setLocale);
  const theme = useCheckoutStore((s) => s.theme);
  const merchant = useCheckoutStore((s) => s.merchant);
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState<string | null>(null);
  const [missingConfig, setMissingConfig] = useState(false);
  const [isApplePayRedirect, setIsApplePayRedirect] = useState(false);
  const [successUrl, setSuccessUrl] = useState<string | null>(null);
  const [completedOrderInfo, setCompletedOrderInfo] = useState<{
    orderId?: string;
    paymentId?: string;
  } | null>(null);
  /**
   * The shop's own order number, carried to the thank-you page.
   *
   * Three of our four identifiers travelled on the return URL — sessionId,
   * orderId, paymentId — and the one the page actually wants to print did not.
   * A thank-you page that wanted to say "Order 1042" had to make a lookup to
   * learn a number the shop itself had supplied.
   */
  const [orderReference, setOrderReference] = useState<string | undefined>(undefined);
  const paymentResult = useCheckoutStore((s) => s.paymentResult);
  const sessionId = useCheckoutStore((s) => s.sessionId);
  const progressPercentage = useProgressPercentage();

  // Parse initialization config from various sources
  const getCheckoutConfig = (): Partial<CheckoutConfig> | null => {
    // 1. Check for global config (set by SDK)
    if (window.SagaPayCheckoutConfig) {
      return window.SagaPayCheckoutConfig;
    }

    // 2. Check URL path for session ID (e.g. /sp_sess_xxx), including the
    //    provider return paths /complete/sp_sess_xxx and /failed/sp_sess_xxx.
    //
    //    The gateway sends the customer back to one of those after paying with
    //    Vipps or Klarna. Matching only the bare form meant the return landed on
    //    a checkout with no session, which told a customer who had just paid to
    //    go back to the shop and try again.
    const pathMatch = window.location.pathname.match(
      /^\/(?:complete\/|failed\/)?(sp_sess_[a-zA-Z0-9_]+)/
    );
    if (pathMatch) {
      const sessionId = pathMatch[1];
      return {
        sessionId,
        partnerId: 'sagapay',
        merchantId: 'from-session', // Will be loaded from session API
        storeId: 'from-session',    // Will be loaded from session API
      };
    }

    // 3. Check URL parameters
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session') || params.get('sessionId');

    // If we have a session ID, we'll fetch the rest from API
    if (sessionId) {
      return {
        sessionId,
        partnerId: params.get('partnerId') || 'sagapay',
        merchantId: params.get('merchantId') || '',
        storeId: params.get('storeId') || '',
      };
    }

    // 4. Check for base64 encoded config in URL
    const configParam = params.get('config');
    if (configParam) {
      try {
        return JSON.parse(atob(configParam));
      } catch (e) {
        // Invalid config
      }
    }

    // 5. Check if embedded mode (loaded via iframe from SDK)
    const embedded = params.get('embedded');
    if (embedded === 'true') {
      // Wait for SDK to send config via postMessage
      return null;
    }

    // 6. No valid config - this is a direct access without SDK
    return null;
  };

  // Listen for PostMessage config from SDK (iframe mode only)
  useEffect(() => {
    // In widget mode, config comes directly — no postMessage needed
    if (widgetMode) return;

    const handleMessage = (event: MessageEvent) => {
      // Handle config from SDK
      if (event.data?.type === 'SAGAPAY_CONFIG') {
        const payload = event.data.payload;
        
        // Apply theme (strip showPoweredBy — branding is mandatory)
        if (payload.theme) {
          const { showPoweredBy: _discard, ...safeTheme } = payload.theme;
          setTheme(safeTheme);
        }
        
        // Apply custom shipping
        if (payload.customShipping) {
          setCustomShippingOptions(payload.customShipping);
          setUseBuiltInShipping(false);
        }
        
        // Apply customer data
        if (payload.customer) {
          setCustomer({
            email: payload.customer.email || '',
            name: `${payload.customer.firstName || ''} ${payload.customer.lastName || ''}`.trim(),
            phone: payload.customer.phone || '',
          });
        }
      }
      
      // Handle legacy config format
      if (event.data?.type === 'SAGAPAY_CHECKOUT_CONFIG') {
        window.SagaPayCheckoutConfig = event.data.config;
        // Trigger re-initialization
        initializeCheckout();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Initialize checkout
  const initializeCheckout = async () => {
    // If a previous mount already completed init, this is a remount
    // caused by store updates during init. Just show the UI immediately.
    if (checkoutInitDone) {
      setIsInitializing(false);
      return;
    }

    // Prevent double execution (e.g. two React roots from double createCheckoutWidget calls)
    if (checkoutInitInProgress) {
      // Don't return with isInitializing=true — wait for the other call to finish
      // The safety timeout will catch if something goes wrong
      return;
    }
    checkoutInitInProgress = true;

    setIsInitializing(true);
    setInitError(null);
    setMissingConfig(false);

    try {
      const config = getCheckoutConfig();
      
      // No config provided - show error
      if (!config) {
        setMissingConfig(true);
        setIsInitializing(false);
        checkoutInitInProgress = false;
        return;
      }
      
      const sessionId = config.sessionId;
      let merchantId = config.merchantId;
      let storeId = config.storeId;
      let cartData = config.cart;
      let customerData = config.customer;
      let sessionSkipShipping = false;
      let sessionShippingAddr: any = null;
      let sessionBillingAddr: any = null;
      /** Where the customer had got to, and whether delivery was settled. */
      let savedStep: string | undefined;
      let shippingSettled = false;
      
      // ── Stale session guard ──────────────────────────
      // Zustand rehydrates from sessionStorage synchronously (before this runs).
      // If the rehydrated sessionId doesn't match the URL sessionId, the store
      // has data from a DIFFERENT checkout (wrong step, stale surfboardOrderId).
      // Reset synchronously so React immediately re-renders at step 'identity'.
      const rehydratedSessionId = useCheckoutStore.getState().sessionId;
      if (rehydratedSessionId && rehydratedSessionId !== sessionId) {
        sessionStorage.removeItem('sagapay-checkout');
        useCheckoutStore.getState().reset();
        resetSdkState();
      } else {
        // Same session but surfboard data may still be stale/expired.
        // Always clear it — PaymentStep will fetch fresh data via /methods/
        useCheckoutStore.getState().setSurfboardData('', '', '');
      }

      // Validate sessionId is present
      if (!sessionId) {
        setInitError('Mangler session ID. Bruk SagaPay SDK for å initiere checkout.');
        setIsInitializing(false);
        checkoutInitInProgress = false;
        return;
      }

      // Reset SDK state when loading a new session
      // This prevents stale payment data from previous sessions
      resetSdkState();

      // Fetch session data from API (single call for all data)
      try {
        const sessionResponse = await getSession(sessionId);
        
        if (sessionResponse.error) {
          const message = sessionResponse.error.message || 'Kunne ikke hente checkout-sesjon';
          setInitError(message);
          // The embedding page has no other way to learn this: the checkout
          // renders an error and otherwise looks mounted.
          notifyParent('CHECKOUT_ERROR', {
            code: sessionResponse.error.code || 'SESSION_LOAD_FAILED',
            message,
            retryable: Boolean(sessionResponse.error.retryable),
          });
          setIsInitializing(false);
          checkoutInitInProgress = false;
          return;
        }
        
        if (sessionResponse.data) {
          const sessionData = sessionResponse.data as any;
          
          if (sessionData.referenceId) setOrderReference(String(sessionData.referenceId));

          // Get merchantId/storeId from session
          merchantId = sessionData.merchantId || merchantId;
          storeId = sessionData.storeId || sessionData.merchantId || storeId;
          
          // Map cart data from API format to UI format
          if (sessionData.cart) {
            const apiCart = sessionData.cart;
            
            cartData = {
              items: (apiCart.orderLines || apiCart.items || []).map((line: any) => ({
                id: line.id,
                name: line.name,
                quantity: line.quantity,
                unitPrice: {
                  amount: line.unitPrice?.value ?? line.unitPrice?.amount ?? line.unitPrice ?? 0,
                  currency: line.unitPrice?.currency ?? apiCart.totalAmount?.currency ?? 'NOK',
                },
                imageUrl: line.imageUrl,
              })),
              totalAmount: {
                amount: apiCart.totalAmount?.value ?? apiCart.totalAmount?.amount ?? 0,
                currency: apiCart.totalAmount?.currency ?? 'NOK',
              },
              totalItems: (apiCart.orderLines || apiCart.items || []).reduce(
                (sum: number, line: any) => sum + (line.quantity || 1), 0
              ),
            };
          }
          
          if (sessionData.customer) {
            customerData = sessionData.customer;
          }
          
          // Extract shipping address from session for pre-fill
          if (sessionData.shippingAddress) {
            sessionShippingAddr = sessionData.shippingAddress;
          }

          if (sessionData.billingAddress) {
            sessionBillingAddr = sessionData.billingAddress;
          }

          savedStep = sessionData.currentStep as string | undefined;
          shippingSettled = Boolean(
            sessionData.skipShipping || sessionData.cart?.skipShipping ||
            sessionData.selectedShipping || sessionData.shippingMethod
          );
          
          // NOTE: Surfboard data (orderId/nonce) is intentionally NOT set here.
          // Session may contain stale/expired Surfboard orders. PaymentStep
          // always fetches fresh, validated data via /v1/payment/methods/
          // which has server-side expired-order detection.
          
          // Check for custom shipping options from session
          if (sessionData.customShippingOptions && sessionData.customShippingOptions.length > 0) {
            // Map to expected format
            const mappedShipping = sessionData.customShippingOptions.map((opt: any) => ({
              id: opt.id,
              name: opt.name,
              description: opt.description || '',
              price: opt.price?.amount ?? 0,
              currency: opt.price?.currency ?? 'NOK',
              logoUrl: opt.logoUrl,
              deliveryDays: opt.deliveryDays,
            }));
            setCustomShippingOptions(mappedShipping);
            setUseBuiltInShipping(false);
          }
          
          // Set locale/market from session (defaults to 'no')
          if (sessionData.locale) {
            setLocale(sessionData.locale);
          }
          

          // Apply branding/theme from session (server-side branding sent by merchant plugin)
          // This is the primary path for redirect mode where SagaPayCheckoutConfig is not set.
          // Session branding uses API format (borderRadius: number, logoWidth: number)
          // while ThemeConfig expects CSS format (borderRadius: "8px", logoHeight: number).
          if (sessionData.branding) {
            const b = sessionData.branding;
            const mappedTheme: any = {};
            if (b.primaryColor) mappedTheme.primaryColor = b.primaryColor;
            if (b.backgroundColor) mappedTheme.backgroundColor = b.backgroundColor;
            if (b.textColor) mappedTheme.textColor = b.textColor;
            if (b.buttonColor) mappedTheme.primaryColor = b.buttonColor; // buttonColor overrides primary
            if (b.fontFamily) mappedTheme.fontFamily = b.fontFamily;
            if (b.logoUrl) mappedTheme.logoUrl = b.logoUrl;
            // Map borderRadius: number (px) -> CSS string
            if (typeof b.borderRadius === 'number') {
              mappedTheme.borderRadius = b.borderRadius + 'px';
              mappedTheme.buttonRadius = b.borderRadius + 'px';
            } else if (typeof b.borderRadius === 'string' && b.borderRadius) {
              mappedTheme.borderRadius = b.borderRadius;
            }
            // Map logoWidth -> logoHeight (ThemeConfig uses logoHeight)
            if (typeof b.logoWidth === 'number') mappedTheme.logoHeight = b.logoWidth;
            if (b.hideHeader !== undefined) mappedTheme.hideHeader = b.hideHeader;
            if (b.hideFooter !== undefined) mappedTheme.hideFooter = b.hideFooter;
            if (b.roundedCorners === false) {
              mappedTheme.borderRadius = '0px';
              mappedTheme.buttonRadius = '0px';
            }
            setTheme(mappedTheme);
          }
          // Save success URL for redirect after payment
          if (sessionData.returnUrl) {
            setSuccessUrl(sessionData.returnUrl);
            
            // NO DOMAIN OVERRIDE NEEDED:
            // The store's paymentPageHostURL is checkout.sagapay.no, matching
            // window.location.host. The SDK computes CHECK_PARAM correctly without
            // any domain override. Previously we set __sagapay_store_domain to
            // the merchant's domain (e.g. panta.no), which broke Apple Pay on PC
            // because the merchant session domainName didn't match the page origin.
          }
          
          // Extract skipShipping from session (will be applied AFTER initSession)
          if (sessionData.cart?.skipShipping || sessionData.skipShipping) {
            sessionSkipShipping = true;
          }
        }
      } catch (e) {
        const message = 'Kunne ikke hente checkout-sesjon. Prøv igjen.';
        setInitError(message);
        notifyParent('CHECKOUT_ERROR', { code: 'SESSION_LOAD_FAILED', message, retryable: true });
        setIsInitializing(false);
        checkoutInitInProgress = false;
        return;
      }
      
      // No CHECKOUT_READY here: one fires below, once the session has been
      // validated, and that is the moment the checkout is actually usable.
      // Adding a second emit is what made onReady fire twice.

      // After session fetch, validate we have data
      const validSessionId: string = sessionId;
      const validMerchantId: string = merchantId || 'unknown';

      // Validate cart data exists
      if (!cartData || !cartData.items || cartData.items.length === 0) {
        setInitError('Handlekurv mangler. Opprett en gyldig checkout-sesjon først.');
        setIsInitializing(false);        checkoutInitInProgress = false;        return;
      }

      // Initialize with cart and session data
      initSession(validSessionId, {
        cart: cartData,
        merchant: {
          merchantId: validMerchantId,
          merchantName: 'Laster...',
          features: {
            enableQuickPay: true,
            enableAddressSaving: true,
            enableVipps: true,
            collectPhone: true,
          },
        },
      });

      // Apply custom shipping options if provided (from SDK config)
      // Note: Session-based shipping already handled above
      if (config.customShipping && config.customShipping.length > 0) {
        setCustomShippingOptions(config.customShipping);
        setUseBuiltInShipping(false);
      } else if (config.useBuiltInShipping !== undefined) {
        setUseBuiltInShipping(config.useBuiltInShipping);
      }
      
      // Skip shipping for digital products (from session or config)
      // Must be set AFTER initSession to avoid being overwritten
      if (sessionSkipShipping || config.skipShipping) {
        setSkipShipping(true);
      }
      
      // Apply theme configuration (strip showPoweredBy — branding is mandatory)
      if (config.theme) {
        const { showPoweredBy: _discard, ...safeTheme } = config.theme;
        setTheme(safeTheme);
      }

      // Set customer if provided (pre-fill from merchant session data)
      if (customerData?.email) {
        // Handle phone which can be string or object { countryCode, number }
        let phoneValue = '';
        if (customerData.phone) {
          if (typeof customerData.phone === 'string') {
            phoneValue = customerData.phone;
          } else if (typeof customerData.phone === 'object') {
            const phoneObj = customerData.phone as { countryCode?: string; number?: string };
            if (phoneObj.number) {
              const countryCode = phoneObj.countryCode || '47';
              phoneValue = `+${countryCode}${phoneObj.number}`;
            }
          }
        }
        // Handle name which can be string or { fullName, firstName, lastName }
        let nameValue = '';
        if (customerData.name) {
          if (typeof customerData.name === 'string') {
            nameValue = customerData.name;
          } else if (typeof customerData.name === 'object') {
            const nameObj = customerData.name as { fullName?: string; firstName?: string; lastName?: string };
            nameValue = nameObj.fullName || [nameObj.firstName, nameObj.lastName].filter(Boolean).join(' ') || '';
          }
        }
        const nameObj = (typeof customerData.name === 'object' ? customerData.name : null) as
          { firstName?: string; lastName?: string } | null;
        setCustomer({
          email: customerData.email,
          name: nameValue,
          // Carried through, not flattened. The shop may state the split
          // itself, and rebuilding it from the joined string guesses wrong for
          // every name that is not exactly two words — which is the fault this
          // was supposed to fix. Dropping it here undid that one step earlier
          // than anyone was looking.
          firstName: nameObj?.firstName,
          lastName: nameObj?.lastName,
          phone: phoneValue,
        });
      }

      // Set shipping address if provided (pre-fill from merchant session data)
      if (sessionShippingAddr) {
        setShippingAddress({
          streetAddress: sessionShippingAddr.streetAddress || sessionShippingAddr.addressLine1 || '',
          careOf: sessionShippingAddr.careOf,
          addressLine2: sessionShippingAddr.addressLine2,
          postalCode: sessionShippingAddr.postalCode || '',
          city: sessionShippingAddr.city || '',
          countryCode: sessionShippingAddr.countryCode || 'NO',
        });
      }

      /**
       * The billing address the shop supplied, if it differs from delivery.
       *
       * `setBillingAddress` existed in the store and had no caller, so the
       * field was always null and the payment step's "use billing when there is
       * one" fell through to the delivery address every single time. A shop
       * that sent both had the one it sent for invoicing replaced by the other,
       * and the order recorded the wrong one.
       */
      if (sessionBillingAddr) {
        setBillingAddress({
          streetAddress: sessionBillingAddr.streetAddress || sessionBillingAddr.addressLine1 || '',
          careOf: sessionBillingAddr.careOf,
          addressLine2: sessionBillingAddr.addressLine2,
          postalCode: sessionBillingAddr.postalCode || '',
          city: sessionBillingAddr.city || '',
          countryCode: sessionBillingAddr.countryCode || 'NO',
        });
      }

      /**
       * Put the customer back where they left off.
       *
       * The session has recorded `currentStep` all along and nothing read it,
       * so someone returning to a payment link two days later — with their
       * name, address and delivery choice all still on the session and all
       * dutifully prefilled — still had to click through three screens of
       * their own answers to reach the pay button. On an invoice link, which
       * exists precisely to be opened later, that is most of the journey.
       *
       * Only forward, and only where the step's own prerequisites are on the
       * session: resuming to payment without an address would drop them on a
       * screen that cannot work, which is worse than one extra click. Anything
       * unrecognised falls back to the beginning.
       */
      if (savedStep && savedStep !== 'identity') {
        const hasEmail = Boolean(customerData?.email);
        const hasAddress = Boolean(sessionShippingAddr?.streetAddress || sessionShippingAddr?.addressLine1);

        const reachable: Record<string, boolean> = {
          address: hasEmail,
          shipping: hasEmail && hasAddress,
          payment: hasEmail && hasAddress && shippingSettled,
        };

        if (reachable[savedStep]) {
          console.log('[SagaPay] Gjenopptar der kunden slapp:', savedStep);
          useCheckoutStore.getState().setStep(savedStep as 'address' | 'shipping' | 'payment');
        }
      }

      // Notify parent that checkout is ready
      notifyParent('CHECKOUT_READY', {
        sessionId: validSessionId,
        step: useCheckoutStore.getState().currentStep,
      });

      // Fetch store details from Surfboard API
      // Terms & privacy URLs are registered in the SagaPay system when the
      // merchant creates their store — they live in onlineInfo and must be
      // fetched from the Surfboard API (not from the checkout session).
      let storeResponse = await getStoreDetails(validMerchantId, storeId, validSessionId);

      // Get payment methods
      const paymentMethodsResponse = await getMerchantPaymentMethods(validMerchantId, validSessionId);

      // Extract activated payment methods
      const activatedMethods = paymentMethodsResponse.data?.data?.map(
        (m) => m.paymentMethod
      ) || ['CARD', 'VIPPS'];

      // NOTE: Do NOT fetch Surfboard SDK data here!
      // Order creation is deferred to PaymentStep to ensure customer phone is available.
      // PaymentStep will call getPaymentMethods() when user reaches that step.
      // This ensures phone number from AddressStep is included in the Surfboard order.


      // Handle store data from Surfboard API
      // Response format: { status: 'SUCCESS', data: { storeId, merchantId, name, onlineInfo: { termsAndConditionsURL, privacyPolicyURL, ... }, email, phone, ... } }
      const storeData = storeResponse.data?.data; // Surfboard nests under .data

      // Fall back to the storeInfo the merchant supplied at session creation.
      // Surfboard is the richer source but it is not always reachable, and a
      // checkout with no terms or privacy link is not one we want to ship —
      // those links are a legal requirement, not decoration.
      const sessionStore = storeData
        ? undefined
        : (await getStoreDetailsFromSession(validSessionId)).data;

      const storeName = storeData?.name ?? sessionStore?.name;
      const resolvedStoreId = storeData?.storeId ?? sessionStore?.storeId;
      const resolvedMerchantId = storeData?.merchantId ?? sessionStore?.merchantId;
      const storeEmail = storeData?.email ?? sessionStore?.supportEmail;
      const storePhone = storeData?.phone ?? sessionStore?.supportPhone;
      const termsUrl = storeData?.onlineInfo?.termsAndConditionsURL ?? sessionStore?.termsAndConditionsURL;
      const privacyUrl = storeData?.onlineInfo?.privacyPolicyURL ?? sessionStore?.privacyPolicyURL;
      const webshopUrl = storeData?.onlineInfo?.merchantWebshopURL ?? sessionStore?.merchantWebshopURL;
      
      // Update merchant with store info
      initSession(validSessionId, {
        cart: cartData,
        merchant: {
          merchantId: resolvedMerchantId || validMerchantId,
          merchantName: storeName || 'Nettbutikk',
          features: {
            enableQuickPay: true,
            enableAddressSaving: true,
            // The API reports Vipps as SVIPPS. Checking only for 'VIPPS' meant
            // this was permanently false, so Vipps never rendered even on
            // terminals that support it.
            enableVipps: activatedMethods.some((m) => m === 'SVIPPS' || m === 'VIPPS'),
            collectPhone: true,
          },
          storeInfo: {
            storeId: resolvedStoreId || storeId || '',
            name: storeName || '',
            email: storeEmail,
            phone: storePhone,
            merchantWebshopURL: webshopUrl,
            termsAndConditionsURL: termsUrl,
            privacyPolicyURL: privacyUrl,
          },
          activatedPaymentMethods: activatedMethods,
        },
      });

    } catch (error) {
      setInitError('Kunne ikke initialisere checkout. Vennligst prøv igjen.');
    }

    setIsInitializing(false);
    checkoutInitDone = true;
    checkoutInitInProgress = false;   // allow re-init (e.g. retry button)
    
    // Apple Pay redirect mode: skip directly to payment step (iframe mode only)
    // In widget mode, Apple Pay works natively — no redirect needed
    const applePayParam = !widgetMode ? new URLSearchParams(window.location.search).get('applepay') : null;
    if (applePayParam === '1') {
      setIsApplePayRedirect(true);
      // Skip to payment step — need all previous steps' data loaded from session
      useCheckoutStore.getState().setStep('payment');
      // Mark previous steps as completed so the progress bar shows correctly
      useCheckoutStore.getState().completeStep('identity');
      useCheckoutStore.getState().completeStep('address');
      useCheckoutStore.getState().completeStep('shipping');
    } else {
      // ── Auto-advance: skip steps that are fully pre-filled ──────────
      // If the merchant sent enough data in session creation, jump the
      // user forward so they don't have to re-enter information.
      const state = useCheckoutStore.getState();
      const cust = state.customer;
      const addr = state.shippingAddress;
      const skip = state.skipShipping;

      // Identity step requires: email + postalCode
      const hasIdentity = !!(cust?.email && addr?.postalCode);
      // Address step requires: name (first+last), streetAddress, city, phone
      const hasAddress = !!(cust?.name?.trim() && addr?.streetAddress?.trim() && addr?.city?.trim() && cust?.phone?.trim());

      // A shipping option the customer already picked counts as done.
      // Without this, reloading on the payment step sent them back to
      // "Velg leveringsmetode" — with the choice still selected — and the card
      // form they were half-way through disappeared.
      const hasShipping = !!state.selectedShipping;

      if (hasIdentity && hasAddress) {
        // All customer + address data present → skip to shipping (or payment)
        useCheckoutStore.getState().completeStep('identity');
        useCheckoutStore.getState().completeStep('address');
        if (skip || hasShipping) {
          useCheckoutStore.getState().completeStep('shipping');
          useCheckoutStore.getState().setStep('payment');
          notifyParent('CHECKOUT_READY', { sessionId: state.sessionId, step: 'payment' });
        } else {
          useCheckoutStore.getState().setStep('shipping');
          notifyParent('CHECKOUT_READY', { sessionId: state.sessionId, step: 'shipping' });
        }
      } else if (hasIdentity) {
        // Only identity data present → skip to address step
        useCheckoutStore.getState().completeStep('identity');
        useCheckoutStore.getState().setStep('address');
        notifyParent('CHECKOUT_READY', { sessionId: state.sessionId, step: 'address' });
      }
    }
  };

  /**
   * Coming back from Vipps, Klarna or MobilePay.
   *
   * The provider sends the customer to /complete/<session> or
   * /failed/<session>. On /complete the payment is real but this browser has
   * never seen it, so we ask the server to confirm it against the API — the
   * same call the card flow makes — and only then show the success screen. It
   * is retried because the gateway can take a moment to register the payment,
   * and a customer who has paid must not be told to pay again.
   */
  useEffect(() => {
    const path = window.location.pathname;
    const ret = path.match(/^\/(complete|failed)\/(sp_sess_[a-zA-Z0-9_]+)/);
    if (!ret) return;

    const [, outcome, returnedSessionId] = ret;

    let cancelled = false;

    /**
     * Initialisation decides which step to show from the session's own data, so
     * anything we set before it finishes is overwritten a moment later — which
     * is exactly what happened: the return message appeared and vanished, and
     * the customer was dropped back on the shipping step. Bounded so a failed
     * init cannot swallow the message entirely.
     */
    const waitForInit = async () => {
      for (let i = 0; i < 60 && !checkoutInitDone && !cancelled; i++) {
        await new Promise(r => setTimeout(r, 250));
      }
    };

    (async () => {
      if (outcome === 'failed') {
        await waitForInit();
        if (cancelled) return;
        useCheckoutStore.getState().setStep('payment');
        useCheckoutStore.getState().setPaymentNotice(
          'Betalingen ble ikke fullført. Du kan prøve igjen eller velge en annen betalingsmåte.'
        );
        return;
      }

      const { completeSession } = await import('./api/client');

      // ~20s in total: long enough for the gateway to catch up, short enough
      // that nobody is left staring at a spinner.
      const waits = [0, 1500, 3000, 5000, 5000, 5000];
      for (const wait of waits) {
        if (cancelled) return;
        if (wait) await new Promise(r => setTimeout(r, wait));

        const response = await completeSession(returnedSessionId);
        if (cancelled) return;

        if (!response.error) {
          // Shape: { event, data: { surfboardOrderId, paymentId, payment: { method } } }
          const event = response.data as unknown as {
            data?: {
              surfboardOrderId?: string;
              paymentId?: string;
              payment?: { method?: string };
            };
          } | undefined;
          const paid = event?.data;

          // The success screen reads the cart and customer from the store, so
          // it needs initialisation to have run.
          await waitForInit();
          if (cancelled) return;

          useCheckoutStore.getState().setPaymentResult({
            success: true,
            status: 'COMPLETED',
            orderId: paid?.surfboardOrderId || '',
            paymentId: paid?.paymentId || '',
            paymentMethod: paid?.payment?.method || outcome,
          });
          return;
        }

        // Anything other than "not paid yet" will not improve with waiting.
        if (response.error.code !== 'PAYMENT_NOT_COMPLETE') break;
      }

      await waitForInit();
      if (cancelled) return;

      // Never claim failure — the money may well have left. Say what we know.
      useCheckoutStore.getState().setStep('payment');
      useCheckoutStore.getState().setPaymentNotice(
        'Vi fikk ikke bekreftet betalingen. Ikke betal på nytt før du har sjekket ' +
        'e-posten din eller kontaktet butikken.'
      );
    })();

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    initializeCheckout();
    
    // Safety net: if init hangs for >15s, force skeleton to disappear.
    // Skip entirely if init already completed (remount scenario).
    if (checkoutInitDone) return;
    
    const safetyTimeout = setTimeout(() => {
      if (checkoutInitDone) return; // init finished while we waited
      setIsInitializing((current) => {
        if (current) {
          setInitError('Checkout tok for lang tid å laste. Prøv å laste siden på nytt.');
        }
        return false;
      });
      checkoutInitInProgress = false;
    }, 15000);
    
    return () => clearTimeout(safetyTimeout);
  }, []);

  // Track payment completion and switch to success step
  useEffect(() => {
    if (paymentResult?.success) {
      setCompletedOrderInfo({
        orderId: paymentResult.orderId,
        paymentId: paymentResult.paymentId,
      });
      
      // Apple Pay redirect mode: notify opener and close window
      if (isApplePayRedirect && window.opener) {
        notifyParent('PAYMENT_COMPLETE', {
          success: true,
          orderId: paymentResult.orderId,
          paymentId: paymentResult.paymentId,
          status: 'COMPLETED',
        });
        // Give the opener a moment to process the message, then close
        setTimeout(() => {
          try { window.close(); } catch (e) { /* some browsers block window.close() */ }
        }, 1500);
      }

      // Apple Pay full-page redirect mode (iOS): no opener, running at top level.
      // SuccessStep will handle the redirect to successUrl automatically.
      // Just notify via postMessage in case anyone is listening.
      if (isApplePayRedirect && !window.opener && window.self === window.top) {
        // Dispatch event for any listening scripts
        window.dispatchEvent(new CustomEvent('sagapay:payment_complete', {
          detail: {
            success: true,
            orderId: paymentResult.orderId,
            paymentId: paymentResult.paymentId,
            status: 'COMPLETED',
          }
        }));
      }
    }
  }, [paymentResult, isApplePayRedirect]);

  const renderStep = () => {
    // Show success step if payment is completed
    if (paymentResult?.success) {
      return (
        <SuccessStep 
          successUrl={successUrl || undefined}
          sessionId={sessionId || undefined}
          orderId={completedOrderInfo?.orderId || paymentResult.orderId}
          paymentId={completedOrderInfo?.paymentId || paymentResult.paymentId}
          reference={orderReference}
        />
      );
    }

    switch (currentStep) {
      case 'identity':
        return <IdentityStep />;
      case 'address':
        return <AddressStep />;
      case 'shipping':
        return <ShippingStep />;
      case 'payment':
        return <PaymentStep />;
      default:
        return <IdentityStep />;
    }
  };

  // Loading state with skeleton
  // Also check checkoutInitDone: if init already completed on a previous mount
  // but this component remounted with fresh useState(true), skip the skeleton.
  if (isInitializing && !checkoutInitDone) {
    return (
      <div className={styles.checkout}>
        <div className={styles.main}>
          <div className={styles.skeletonContainer}>
            {/* Skeleton header */}
            <div className={styles.skeletonHeader}>
              <div className={styles.skeletonLogo} />
            </div>
            
            {/* Skeleton progress bar */}
            <div className={styles.skeletonProgress}>
              <div className={styles.skeletonProgressBar} />
              <div className={styles.skeletonSteps}>
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className={styles.skeletonStep}>
                    <div className={styles.skeletonDot} />
                    <div className={styles.skeletonLabel} />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Skeleton form */}
            <div className={styles.skeletonForm}>
              <div className={styles.skeletonTitle} />
              <div className={styles.skeletonSubtitle} />
              <div className={styles.skeletonInput} />
              <div className={styles.skeletonInput} />
              <div className={styles.skeletonButton} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (initError) {
    return (
      <div className={styles.checkout}>
        <div className={styles.errorContainer}>
          <svg className={styles.errorIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8v4M12 16h.01"/>
          </svg>
          <p className={styles.errorText}>{initError}</p>
          <button className={styles.retryButton} onClick={() => initializeCheckout()}>
            Prøv igjen
          </button>
        </div>
      </div>
    );
  }

  // Missing config state - direct access without SDK
  if (missingConfig) {
    // Collect debug info — log to console only (not shown to users)
    const debugInfo = {
      url: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      inIframe: window.self !== window.top,
      hasSagaPayConfig: !!window.SagaPayCheckoutConfig,
      referrer: document.referrer || 'none',
      params: Object.fromEntries(new URLSearchParams(window.location.search).entries()),
    };
    console.warn('[SagaPay] Missing config — debug info:', debugInfo);
    return (
      <div className={styles.checkout}>
        <div className={styles.errorContainer}>
          <svg className={styles.errorIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: '#ff5b24' }}>
            <path d="M12 2L2 7l10 5 10-5-10-5z"/>
            <path d="M2 17l10 5 10-5"/>
            <path d="M2 12l10 5 10-5"/>
          </svg>
          <h2 className={styles.errorTitle}>SagaPay Checkout</h2>
          <p className={styles.errorText}>
            Denne siden kan kun åpnes via en nettbutikk.<br/>
            Gå tilbake til butikken og prøv igjen.
          </p>
          {/* Debug info only logged to console, not shown to users */}
        </div>
      </div>
    );
  }

  // Get theme config for conditional rendering
  // Note: showPoweredBy is NOT configurable — SagaPay branding is always shown
  const showOrderSummary = theme?.showOrderSummary !== false;
  const merchantLogo = theme?.logoUrl || merchant?.logoUrl;
  const isWidgetMode = widgetMode || !!(window as any).__SAGAPAY_WIDGET_MODE__;

  return (
    <div className={`${styles.checkout} ${isWidgetMode ? styles.checkoutWidget : ''}`}>
      {/* Optional merchant logo header */}
      {merchantLogo && (
        <header className={styles.merchantHeader} style={{ justifyContent: theme?.logoPosition || 'left' }}>
          <img 
            src={merchantLogo} 
            alt={merchant?.merchantName || 'Logo'} 
            className={styles.merchantLogo}
            style={{ height: theme?.logoHeight || 32 }}
          />
        </header>
      )}
      
      <main className={styles.main}>
        {/* Progress bar - hide on success */}
        {!paymentResult?.success && (
          <ProgressBar progress={progressPercentage} currentStep={currentStep} onStepClick={(step) => useCheckoutStore.getState().setStep(step)} />
        )}
        <div className={styles.content}>
          {renderStep()}
        </div>
      </main>
      
      {showOrderSummary && <OrderSummary />}
      
      <footer className={`${styles.footer} ${isWidgetMode ? styles.footerWidget : ''}`}>
        {(merchant?.storeInfo?.termsAndConditionsURL || merchant?.storeInfo?.privacyPolicyURL) && (
          <div className={styles.footerLinks}>
            {merchant?.storeInfo?.termsAndConditionsURL && (
              <a 
                href={merchant.storeInfo.termsAndConditionsURL} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Kjøpsvilkår
              </a>
            )}
            {merchant?.storeInfo?.termsAndConditionsURL && merchant?.storeInfo?.privacyPolicyURL && (
              <span>•</span>
            )}
            {merchant?.storeInfo?.privacyPolicyURL && (
              <a 
                href={merchant.storeInfo.privacyPolicyURL} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                Personvernerklæring
              </a>
            )}
          </div>
        )}
        {/* Powered by SagaPay — always shown, not configurable */}
        <div className={styles.poweredBy} data-sagapay-branding="true">
          <span>Drevet av</span>
          <img 
            src="https://res.cloudinary.com/martinsurf/image/upload/v1763562569/surfboardmerchant/cexdplrnd3tr54xhjjvt.png" 
            alt="Saga" 
            className={styles.sagaLogo}
          />
        </div>
      </footer>
    </div>
  );
}

// Main App with ThemeProvider wrapper
export interface AppProps {
  widgetMode?: boolean;
}
export default function App({ widgetMode }: AppProps) {
  const theme = useCheckoutStore((s) => s.theme);
  
  return (
    <ErrorBoundary>
      <ThemeProvider config={theme || undefined} widgetMode={widgetMode}>
        <CheckoutContent widgetMode={widgetMode} />
      </ThemeProvider>
    </ErrorBoundary>
  );
}
